import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users,
  Plus,
  Trash2,
  Edit3,
  Loader2,
  Search,
  X,
  Shield,
  Eye,
  Building2,
  Mail,
  Box,
  Pencil,
  KeyRound,
  Wrench
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: { label: 'Super Admin', color: 'bg-red-500/10 text-red-500 border-red-500/30' },
  editor: { label: 'Editor', color: 'bg-blue-500/10 text-blue-500 border-blue-500/30' },
  viewer: { label: 'Viewer', color: 'bg-green-500/10 text-green-500 border-green-500/30' },
  maintenance_tech: { label: 'Maintenance Tech', color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30' },
};

export function UserManagementPanel({ isOpen, onClose }) {
  const { token } = useAuth();
  const { tours, fetchTours } = useTour();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClientFilter, setSelectedClientFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'viewer',
    password: '',
    client_id: '',
    tours_access: []
  });

  // Fetch clients
  const fetchClients = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  }, [token]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchTours();
      fetchClients();
    }
  }, [isOpen, fetchUsers, fetchTours, fetchClients]);

  // Filter users by client and search
  const filteredUsers = users.filter(user => {
    // First filter by selected client
    if (selectedClientFilter !== 'all') {
      if (selectedClientFilter === 'no-client') {
        if (user.client_id) return false;
      } else {
        if (user.client_id !== selectedClientFilter) return false;
      }
    }
    // Then filter by search query
    if (searchQuery) {
      return user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // Get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'No client';
  };

  // Create user
  const handleCreate = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (res.ok) {
        await fetchUsers();
        setNewUser({
          email: '',
          name: '',
          role: 'viewer',
          password: '',
          client_id: '',
          tours_access: []
        });
        setIsCreateDialogOpen(false);
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to create user');
      }
    } catch (err) {
      console.error('Failed to create user:', err);
    } finally {
      setSaving(false);
    }
  };

  // Update user
  const handleUpdate = async () => {
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${editingUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingUser.name,
          client_id: editingUser.client_id,
          role: editingUser.role,
          tours_access: editingUser.tours_access,
          is_active: editingUser.is_active
        })
      });

      if (res.ok) {
        await fetchUsers();
        setEditingUser(null);
      }
    } catch (err) {
      console.error('Failed to update user:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchUsers();
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  // Reset user password
  const handleResetPassword = async () => {
    if (!editingUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${editingUser.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      if (res.ok) {
        alert('Password reset successfully');
        setNewPassword('');
        setShowResetPassword(false);
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Toggle tour access
  const toggleTourAccess = (tourId, isEditing = false) => {
    if (isEditing && editingUser) {
      const current = editingUser.tours_access || [];
      const updated = current.includes(tourId)
        ? current.filter(id => id !== tourId)
        : [...current, tourId];
      setEditingUser({ ...editingUser, tours_access: updated });
    } else {
      const current = newUser.tours_access || [];
      const updated = current.includes(tourId)
        ? current.filter(id => id !== tourId)
        : [...current, tourId];
      setNewUser({ ...newUser, tours_access: updated });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            User Management
          </SheetTitle>
          <SheetDescription>
            Manage users and their tour access permissions
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Client Filter */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Filter by Client</Label>
            <Select 
              value={selectedClientFilter} 
              onValueChange={(value) => {
                setSelectedClientFilter(value);
                // Auto-set client_id for new users when a client is selected
                if (value !== 'all' && value !== 'no-client') {
                  setNewUser(prev => ({ ...prev, client_id: value }));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="no-client">Super Admins (No Client)</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3 w-3" />
                      {client.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search and Add */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-1">
              <Plus className="h-4 w-4" />
              Add User
            </Button>
          </div>

          {/* Users List */}
          <ScrollArea className="h-[calc(100vh-220px)]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No users found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="p-4 rounded-lg border bg-card hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground truncate">
                            {user.name}
                          </h4>
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] ${ROLE_LABELS[user.role]?.color}`}
                          >
                            {ROLE_LABELS[user.role]?.label}
                          </Badge>
                          {!user.is_active && (
                            <Badge variant="outline" className="text-[10px] bg-yellow-500/10 text-yellow-500">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </p>
                        {user.client_id && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Building2 className="h-3 w-3" />
                            {getClientName(user.client_id)}
                          </p>
                        )}
                        {user.tours_access?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            <Box className="h-3 w-3 text-primary" />
                            <span className="text-xs text-primary">
                              {user.tours_access.length} tour{user.tours_access.length !== 1 ? 's' : ''} assigned
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditingUser({ ...user })}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleDelete(user.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Create User Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user and assign their tour access
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="user@company.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  placeholder="Full name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Client *</Label>
                <Select 
                  value={newUser.client_id} 
                  onValueChange={(value) => setNewUser({ ...newUser, client_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3 w-3" />
                          {client.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">User will have access to all tours assigned to this client</p>
              </div>

              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  placeholder="Temporary password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">
                      <div className="flex items-center gap-2">
                        <Eye className="h-3 w-3" />
                        Viewer (View only)
                      </div>
                    </SelectItem>
                    <SelectItem value="maintenance_tech">
                      <div className="flex items-center gap-2">
                        <Wrench className="h-3 w-3" />
                        Maintenance Tech (View + Log maintenance)
                      </div>
                    </SelectItem>
                    <SelectItem value="editor">
                      <div className="flex items-center gap-2">
                        <Pencil className="h-3 w-3" />
                        Editor (Can edit)
                      </div>
                    </SelectItem>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-3 w-3" />
                        Admin (Full access)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tour Access */}
              <div className="space-y-2">
                <Label>Tour Access</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {tours.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tours available</p>
                  ) : (
                    tours.map((tour) => (
                      <div key={tour.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`tour-${tour.id}`}
                          checked={newUser.tours_access.includes(tour.id)}
                          onCheckedChange={() => toggleTourAccess(tour.id)}
                        />
                        <label 
                          htmlFor={`tour-${tour.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {tour.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={!newUser.email || !newUser.name || !newUser.password || saving}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={!!editingUser} onOpenChange={(open) => {
          if (!open) {
            setEditingUser(null);
            setShowResetPassword(false);
            setNewPassword('');
          }
        }}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and tour access
              </DialogDescription>
            </DialogHeader>
            
            {editingUser && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingUser.email}
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select 
                    value={editingUser.client_id || ''} 
                    onValueChange={(value) => setEditingUser({ ...editingUser, client_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3 w-3" />
                            {client.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">User will have access to tours assigned to this client</p>
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select 
                    value={editingUser.role} 
                    onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="h-3 w-3" />
                          Viewer (View only)
                        </div>
                      </SelectItem>
                      <SelectItem value="maintenance_tech">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-3 w-3" />
                          Maintenance Tech (View + Log maintenance)
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-2">
                          <Pencil className="h-3 w-3" />
                          Editor (Can edit)
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3 w-3" />
                          Admin (Full access)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is-active"
                      checked={editingUser.is_active}
                      onCheckedChange={(checked) => 
                        setEditingUser({ ...editingUser, is_active: checked })
                      }
                    />
                    <label htmlFor="is-active" className="text-sm">
                      Account active
                    </label>
                  </div>
                </div>

                {/* Password Reset Section */}
                <div className="space-y-2">
                  <Label>Password</Label>
                  {!showResetPassword ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowResetPassword(true)}
                      className="w-full gap-2"
                    >
                      <KeyRound className="h-4 w-4" />
                      Reset Password
                    </Button>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                      <Input
                        type="password"
                        placeholder="Enter new password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleResetPassword}
                          disabled={resettingPassword || newPassword.length < 6}
                          className="flex-1"
                        >
                          {resettingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Set New Password
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowResetPassword(false);
                            setNewPassword('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Note: Passwords are stored securely and cannot be viewed
                      </p>
                    </div>
                  )}
                </div>

                {/* Tour Access */}
                <div className="space-y-2">
                  <Label>Tour Access</Label>
                  <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                    {tours.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No tours available</p>
                    ) : (
                      tours.map((tour) => (
                        <div key={tour.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`edit-tour-${tour.id}`}
                            checked={editingUser.tours_access?.includes(tour.id)}
                            onCheckedChange={() => toggleTourAccess(tour.id, true)}
                          />
                          <label 
                            htmlFor={`edit-tour-${tour.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {tour.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingUser(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default UserManagementPanel;
